/**
 * @module beebee-base/lib/utility/relationships
 * @description Relationship (added/removed related) helpers.
 *
 * CONTRACT: both helpers take the RECORD THAT OWNS the relationship, never the
 * relationship itself. They write two bookkeeping fields onto that record —
 * `addedRelated` / `removedRelated` — and `saveRecord` gates the
 * `/sync_relations` POST on exactly those two fields
 * (controllers/application.js: `if (record.id && (record.addedRelated ||
 * record.removedRelated))`). Bookkeeping written anywhere else is never read by
 * anyone, so the user's pick is dropped without a word.
 *
 * Passing the relationship instead of its owner is the mistake this file guards
 * against: it shipped to production once (Sentry MAISON-VIVE-1F / -9,
 * `item.get('invoicegroups')` handed to `addRelationship`). Under ember-data 5.8
 * a sync hasMany is an `IdentifierArray` whose proxy refuses the write, so the
 * user saw "'set' on proxy: trap returned falsish for property 'addedRelated'" —
 * a message naming neither the helper nor the caller.
 *
 * WHY A THROW AND NOT A DEV-ONLY ASSERT OR A LOGGED NO-OP
 * A no-op would turn a loud crash into silent data loss, which is the worse of
 * the two failure modes and the one this codebase already fights elsewhere. A
 * dev-only `assert` from '@ember/debug' is stripped from production builds,
 * which is exactly where the incident happened and where the unattributable
 * message cost the time. Throwing is also what the neighbouring utilities do for
 * a broken precondition (see `lib/utility/ajax.js`, `throw new Error('Error no
 * url')`); `@ember/debug`'s `assert` is used nowhere in this addon's source.
 * Nothing that works today starts throwing: every shape rejected below already
 * throws — from inside ember-data or as `parent.set is not a function` — with a
 * message that does not say who called what.
 */

/**
 * Array-like covers every way a relationship reaches a caller: a plain array,
 * ember-data 5.8's `IdentifierArray` / `ManyArray`, and `PromiseManyArray`.
 * A record is none of these.
 */
function isArrayLike(value) {
  return Array.isArray(value) || typeof value.length === 'number' && typeof value.forEach === 'function';
}
function describeArgument(value) {
  if (Array.isArray(value)) return 'an array';
  if (isArrayLike(value)) return `an array-like (${value.constructor?.name || 'unknown type'})`;
  if (typeof value !== 'object') return `a ${typeof value}`;
  return `an object with no set() method (${value.constructor?.name || 'plain object'})`;
}

/**
 * The parent must be a single record that can take the bookkeeping.
 * @param {string} helper - name of the calling helper, for the message
 * @param {Object} parent
 */
function assertRelationshipOwner(helper, parent) {
  if (isArrayLike(parent) || typeof parent.set !== 'function') {
    throw new Error(`${helper}(parent, item): parent must be the record that owns the relationship, ` + `but it got ${describeArgument(parent)}. ` + `Pass the record (e.g. \`item\`), not the relationship itself ` + `(e.g. \`item.get('invoicegroups')\`): addedRelated/removedRelated written ` + `anywhere but on the owning record is never synced by saveRecord.`);
  }
}

/**
 * The item ends up in addedRelated/removedRelated, which `groupRelationsByModel`
 * reads `constructor.modelName` and `id` off. An array has neither, so it would
 * be POSTed to /sync_relations under model `undefined`.
 * @param {string} helper - name of the calling helper, for the message
 * @param {Object} item
 */
function assertRelationshipItem(helper, item) {
  if (isArrayLike(item)) {
    throw new Error(`${helper}(parent, item): item must be a single record, but it got ` + `${describeArgument(item)}. Add or remove one record per call — the ` + `bookkeeping is grouped by constructor.modelName and id, which an array ` + `does not have.`);
  }
}

/**
 * Aggiunge una relazione a un record parent
 * @param {Object} parent - Record parent
 * @param {Object} item - Item da aggiungere
 * @returns {Object} Parent modificato
 */
function addRelationship(parent, item) {
  if (!parent || !item) return parent;
  assertRelationshipOwner('addRelationship', parent);
  assertRelationshipItem('addRelationship', item);
  const addedRelated = parent.addedRelated || [];
  const removedRelated = parent.removedRelated || [];
  if (!addedRelated.includes(item)) {
    addedRelated.push(item);
    parent.set('addedRelated', addedRelated);
  }
  const removedIdx = removedRelated.indexOf(item);
  if (removedIdx !== -1) {
    removedRelated.splice(removedIdx, 1);
    parent.set('removedRelated', removedRelated);
  }
  return parent;
}

/**
 * Rimuove una relazione da un record parent
 * @param {Object} parent - Record parent
 * @param {Object} item - Item da rimuovere
 * @returns {Object} Parent modificato
 */
function delRelationship(parent, item) {
  if (!parent || !item) return parent;
  assertRelationshipOwner('delRelationship', parent);
  assertRelationshipItem('delRelationship', item);
  const addedRelated = parent.addedRelated || [];
  const removedRelated = parent.removedRelated || [];
  if (!removedRelated.includes(item)) {
    removedRelated.push(item);
    parent.set('removedRelated', removedRelated);
  }
  const addedIdx = addedRelated.indexOf(item);
  if (addedIdx !== -1) {
    addedRelated.splice(addedIdx, 1);
    parent.set('addedRelated', addedRelated);
  }
  return parent;
}

export { addRelationship, delRelationship };
//# sourceMappingURL=relationships.js.map
